import _ from 'lodash';
import clsx from 'clsx';
import React, { FunctionComponent } from 'react';
import { ProductType } from 'cart-model';
import { useTranslation } from 'react-i18next';
import { Card, CurrencyInput, formatCurrency, Slider, Typography, Label, FieldErrorMessage, VisuallyHidden } from 'ui-components';
import { LimitsValidation } from '../LimitsValidation/LimitsValidation';

import useStyles from './ChooseInvestmentSliders.style';

const DEFAULT_MAX_AMOUNT = 50000;

const finiteOrDefault = (possiblyInfinite: number) => _.isFinite(possiblyInfinite) ? possiblyInfinite : DEFAULT_MAX_AMOUNT;

const ChooseInvestmentSliders: FunctionComponent<{
  monthlyAmount: number,
  initialAmount: number,
  onMonthlyAmountChange: (value: number) => void,
  onInitialAmountChange: (value: number) => void,
  limitsValidation: LimitsValidation
  productType: ProductType
}> = ({ monthlyAmount, initialAmount, onMonthlyAmountChange, onInitialAmountChange, limitsValidation, productType }) => {

  const c = useStyles({});
  const [t] = useTranslation('choose-investment-amount');

  const initialAmountHasError = initialAmount < limitsValidation.initialAmountMin || initialAmount > limitsValidation.initialAmountMax;
  const monthlyAmountHasError = monthlyAmount > limitsValidation.monthlyAmountMax;

  let initialAmountErrorMessage: string | undefined = undefined;
  let monthlyAmountErrorMessage: string | undefined = undefined;

  if (initialAmount < limitsValidation.initialAmountMin) {
    initialAmountErrorMessage = t('min-amount-error', {
      min: formatCurrency(limitsValidation.initialAmountMin)
    });
  } else if (initialAmount > limitsValidation.initialAmountMax) {
    initialAmountErrorMessage = t('max-amount-error', {
      max: formatCurrency(limitsValidation.initialAmountMax)
    });
  }

  if (monthlyAmount > limitsValidation.monthlyAmountMax) {
    monthlyAmountErrorMessage = t('max-amount-error', {
      max: formatCurrency(limitsValidation.monthlyAmountMax)
    });
  } else if (monthlyAmount < (limitsValidation.monthlyAmountMin ?? 0)) {
    monthlyAmountErrorMessage = t('min-amount-error', {
      min: formatCurrency(limitsValidation.monthlyAmountMin ?? 0)
    });
  }

  return (
    <Card className={c.root}>
      <Typography variant="h3" fontWeight="bold" textColor="greys.80">{t('sliders-header')}</Typography>
      {(productType === ProductType.ike || productType === ProductType.ikze) && (
        <Typography textColor="greys.60" className={c.slidersCaption}>
          {t('sliders-caption',
            {
              year: new Date().getFullYear(),
              yearlyLimit: formatCurrency(limitsValidation.initialAmountMax),
              limitPerMonth: formatCurrency(limitsValidation.initialAmountMax / 12),
              productType: productType === ProductType.ike ? 'IKE' : 'IKZE'
            })}
        </Typography>
      )}
      <div className={c.container}>
        <div className={c.chooseInvestmentWrapper}>
          <ChooseInvestmentSlider amount={initialAmount}
            setAmount={onInitialAmountChange}
            namespace="initial"
            min={limitsValidation.initialAmountMin}
            max={finiteOrDefault(limitsValidation.initialAmountMax)}
            hasError={initialAmountHasError}
            errorMessage={initialAmountErrorMessage}
          />
        </div>
        <div className={clsx(c.chooseInvestmentWrapper, c.chooseInvestmentWrapperMonthlyPaymant)}>
          <ChooseInvestmentSlider amount={monthlyAmount}
            setAmount={onMonthlyAmountChange}
            namespace="monthly"
            min={0}
            max={finiteOrDefault(limitsValidation.monthlyAmountMax)}
            data-testid="monthlyAmountSlider"
            hasError={monthlyAmountHasError}
            errorMessage={monthlyAmountErrorMessage}
          />
        </div>
      </div>
      <Typography>{t('caption')}</Typography>
    </Card>
  );
};

const ChooseInvestmentSlider: FunctionComponent<{
  amount: number
  setAmount: (value: number) => void
  namespace: string,
  min: number,
  max: number,
  hasError?: boolean,
  errorMessage?: string,
}> = ({ amount, setAmount, namespace, min, max, hasError = false, errorMessage }) => {
  const [t] = useTranslation('choose-investment-amount');
  const c = useStyles({});

  return (<>
    <div className={c.sliderWrapper}>
      <div>
        <Typography>{t(`${namespace}.slider-header`)}</Typography>
        <Typography variant="body2" textColor="greys.60" className={c.sliderCaption}> {t(`${namespace}.slider-caption`, { min })} </Typography>
      </div>
      <div className={c.sliderInputWrapper}>
        <VisuallyHidden>
          <Label as="label" htmlFor={`${namespace}-slider`}>
            <Typography> {t(`${namespace}.slider-header`)}</Typography>
            <Typography variant="body2" textColor="greys.60"> {t(`${namespace}.slider-caption`, { min })} </Typography>
          </Label>
        </VisuallyHidden>
        <CurrencyInput value={amount}
          onValueChange={value => setAmount(value)} id={`${namespace}-slider`} hasError={hasError} />
        {hasError && errorMessage && (
          <FieldErrorMessage id={`${namespace}-error`} className={c.errorMessage}>
            {errorMessage}
          </FieldErrorMessage>
        )}
      </div>
    </div>
    <Slider
      testId="amountSlider"
      amount={amount || 0}
      setAmount={(value) => setAmount(value)}
      min={min}
      max={max}
      marks={[{ value: min, label: formatCurrency(min) }, { value: max, label: formatCurrency(max) }]}
    />
  </>);
};

export { ChooseInvestmentSliders }
